/**
 * React Query hooks for the team management endpoints (M3.9).
 */
import { useAuth } from "@clerk/clerk-react";
import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  ApiError,
  CreateInviteRequest,
  CreateInviteResponse,
  TeamInvitesResponse,
  TeamMembersResponse,
} from "@/lib/api-client";
import {
  createTeamInvite,
  fetchTeamInvites,
  fetchTeamMembers,
  revokeTeamInvite,
} from "@/lib/api-client";

export const teamKeys = {
  all: ["team"] as const,
  members: () => ["team", "members"] as const,
  invites: () => ["team", "invites"] as const,
} as const;

// ─── Members ──────────────────────────────────────────────────────────────────

export type UseTeamMembersResult = UseQueryResult<TeamMembersResponse, ApiError | Error>;

export function useTeamMembers(): UseTeamMembersResult {
  const { getToken } = useAuth();
  return useQuery<TeamMembersResponse, ApiError | Error>({
    queryKey: teamKeys.members(),
    queryFn: () => fetchTeamMembers({ getToken: () => getToken() }),
  });
}

// ─── Invites ──────────────────────────────────────────────────────────────────

export type UseTeamInvitesResult = UseQueryResult<TeamInvitesResponse, ApiError | Error>;

export function useTeamInvites(): UseTeamInvitesResult {
  const { getToken } = useAuth();
  return useQuery<TeamInvitesResponse, ApiError | Error>({
    queryKey: teamKeys.invites(),
    queryFn: () => fetchTeamInvites({ getToken: () => getToken() }),
  });
}

export type UseCreateInviteResult = UseMutationResult<
  CreateInviteResponse,
  ApiError | Error,
  CreateInviteRequest
>;

export function useCreateInvite(): UseCreateInviteResult {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation<CreateInviteResponse, ApiError | Error, CreateInviteRequest>({
    mutationFn: (body) => createTeamInvite(body, { getToken: () => getToken() }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: teamKeys.invites() });
    },
  });
}

export type UseRevokeInviteResult = UseMutationResult<void, ApiError | Error, string>;

export function useRevokeInvite(): UseRevokeInviteResult {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation<void, ApiError | Error, string>({
    mutationFn: (id) => revokeTeamInvite(id, { getToken: () => getToken() }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: teamKeys.invites() });
    },
  });
}
